import { resolvePath } from '@inference.engine/widget-html/web';

export interface DeclaredAction {
  name: string;
  /** The text of the button that runs it, or its name when no button does. */
  label: string;
  type: string;
  /** The full prompt, with its placeholders, as the person would send it. */
  prompt: string;
}

const ACTION = /<mf-action\b([^>]*)>([\s\S]*?)<\/mf-action\s*>/g;
const attribute = (tag: string, name: string) =>
  new RegExp(`\\s${name}\\s*=\\s*"([^"]*)"`).exec(tag)?.[1] ?? '';

const buttonText = (document: string, name: string) => {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const button = new RegExp(
    `<(\\w+)[^>]*\\sdata-action\\s*=\\s*"${escaped}"[^>]*>([\\s\\S]*?)</\\1\\s*>`,
  ).exec(document);
  const text = button?.[2]
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return text || name;
};

/** The actions a widget declares, in document order. */
/* eslint-disable i18next/no-literal-string -- HTML attribute names */
export function declaredActions(document: string): DeclaredAction[] {
  return [...document.matchAll(ACTION)].map((match) => ({
    name: attribute(match[1], 'name'),
    label: buttonText(document, attribute(match[1], 'name')),
    type: attribute(match[1], 'type'),
    prompt: match[2].replace(/\s+/g, ' ').trim(),
  }));
}
/* eslint-enable i18next/no-literal-string */

export type PromptPart = { text: string } | { placeholder: string };

const PLACEHOLDER = /\{\{\s*([^{}]+?)\s*\}\}/g;

/** A prompt's plain text and its placeholders, in order. */
export function promptParts(prompt: string): PromptPart[] {
  const parts: PromptPart[] = [];
  let last = 0;
  for (const match of prompt.matchAll(PLACEHOLDER)) {
    if (match.index > last) parts.push({ text: prompt.slice(last, match.index) });
    parts.push({ placeholder: match[1] });
    last = match.index + match[0].length;
  }
  if (last < prompt.length) parts.push({ text: prompt.slice(last) });
  return parts;
}

/** The prompt as the person would send it with this data; a placeholder with no value stays. */
export function fillPrompt(prompt: string, data: unknown): string {
  return prompt.replace(PLACEHOLDER, (whole, path: string) => {
    const value = resolvePath(data, path);
    return value === undefined || value === null || typeof value === 'object'
      ? whole
      : String(value);
  });
}
